import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, X, Sparkles, Printer } from "lucide-react";
import { inr2, fmtDate, today } from "@/lib/format";
import { printInvoice } from "@/lib/print";
import { toast } from "sonner";

const STATUSES = ["Unpaid", "Paid", "Overdue"];
const emptyForm = () => ({
  customer_id: "",
  customer_name: "",
  status: "Unpaid",
  tax_percent: 18,
  issue_date: today(),
  due_date: "",
  notes: "",
  items: [{ description: "", quantity: 1, rate: 0 }],
});

export default function Invoices() {
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const loadAll = async () => {
    try {
      const [inv, cust] = await Promise.all([api.get("/invoices"), api.get("/customers")]);
      setRows(inv.data);
      setCustomers(cust.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    loadAll();
  }, []);

  const subtotal = form.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.rate) || 0), 0);
  const tax = (subtotal * (Number(form.tax_percent) || 0)) / 100;
  const total = subtotal + tax;

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...emptyForm(), ...row, items: row.items?.length ? row.items : [{ description: "", quantity: 1, rate: 0 }] });
    setOpen(true);
  };

  const setItem = (idx, key, value) => {
    const items = form.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, rate: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!form.customer_id) {
      toast.error("Please select a customer");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity) || 0,
          rate: Number(i.rate) || 0,
        })),
      };
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editing ? "Invoice updated" : "Invoice created");
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/invoices/${row.id}`);
      toast.success("Invoice deleted");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const genSummary = async (row) => {
    setAiOpen(true);
    setAiText("");
    setAiLoading(true);
    try {
      const res = await api.post("/ai/invoice-summary", { invoice_id: row.id });
      setAiText(res.data.summary);
    } catch (e) {
      setAiText("Could not generate summary. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle="Bill customers and track payments. Generate AI email summaries in one click."
        action={
          <Button data-testid="add-invoice-btn" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" /> New Invoice
          </Button>
        }
      />

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {["Invoice", "Customer", "Issue date", "Due date", "Total", "Status", "AI"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  {h}
                </TableHead>
              ))}
              <TableHead className="w-[90px] text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-12 text-center text-muted-foreground">
                  No invoices yet. Create your first invoice.
                </TableCell>
              </TableRow>
            )}
            {rows &&
              rows.map((r) => (
                <TableRow key={r.id} data-testid="invoices-row">
                  <TableCell className="font-medium">{r.invoice_no}</TableCell>
                  <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.issue_date)}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.due_date)}</TableCell>
                  <TableCell className="text-sm font-medium tabular-nums">{inr2(r.total)}</TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1 text-xs"
                      onClick={() => genSummary(r)}
                      data-testid="ai-summary-btn"
                    >
                      <Sparkles className="h-3.5 w-3.5 text-primary" /> Summary
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title="Print bill" onClick={() => printInvoice(r, customers.find((c) => c.id === r.customer_id) || customers.find((c) => c.name === r.customer_name))} data-testid="print-invoice-btn">
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} data-testid="edit-invoice-btn">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => remove(r)}
                        data-testid="delete-invoice-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableShell>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[680px]">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Invoice" : "New Invoice"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label>Customer</Label>
                <Select
                  value={form.customer_id}
                  onValueChange={(v) => {
                    const c = customers.find((x) => x.id === v);
                    setForm({ ...form, customer_id: v, customer_name: c ? c.name : "" });
                  }}
                >
                  <SelectTrigger data-testid="invoice-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="invoice-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Issue date</Label>
                <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Due date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Line items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-line-item">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add item
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={idx} className="flex items-end gap-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Description (e.g. Freight Mumbai → Delhi)"
                        value={it.description}
                        data-testid={`item-desc-${idx}`}
                        onChange={(e) => setItem(idx, "description", e.target.value)}
                      />
                    </div>
                    <div className="w-16">
                      <Input type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} />
                    </div>
                    <div className="w-28">
                      <Input type="number" placeholder="Rate" value={it.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-destructive"
                      onClick={() => removeItem(idx)}
                      disabled={form.items.length === 1}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label>Tax %</Label>
                <Input type="number" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: Number(e.target.value) })} />
              </div>
            </div>

            <Textarea
              placeholder="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />

            <div className="rounded-md border border-border bg-slate-50 p-4 text-sm">
              <div className="flex justify-between py-0.5">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">{inr2(subtotal)}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-muted-foreground">Tax ({form.tax_percent || 0}%)</span>
                <span className="tabular-nums">{inr2(tax)}</span>
              </div>
              <div className="mt-1 flex justify-between border-t border-border pt-2 font-display text-base font-bold">
                <span>Total</span>
                <span className="tabular-nums" data-testid="invoice-total">{inr2(total)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-invoice-btn">
              {saving ? "Saving…" : "Save Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI summary dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Sparkles className="h-4 w-4 text-primary" /> AI Invoice Summary
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-[100px] rounded-md border border-border bg-slate-50 p-4 text-sm leading-relaxed">
            {aiLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-11/12" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : (
              <p data-testid="ai-summary-text">{aiText}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard?.writeText(aiText);
                toast.success("Copied to clipboard");
              }}
              disabled={!aiText || aiLoading}
            >
              Copy
            </Button>
            <Button onClick={() => setAiOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
